import {
  appsDeficitsApiGetProjectDeficits,
  appsWorkordersApiListWorkOrders,
  getAppsDeficitsApiGetProjectDeficitsQueryKey,
  getAppsWorkordersApiListWorkOrdersQueryKey,
} from '@/lib/api/generated/endpoints'
import type { DeficitsResponse } from '@/lib/api/generated/types.ts/deficitsResponse'
import type { PaginatedWorkOrders } from '@/lib/api/generated/types.ts/paginatedWorkOrders'
import { queryOptions } from '@tanstack/react-query'

/**
 * Dashboard queries — three independent KPIs fetched in parallel.
 *
 * Why hand-rolled (not orval `*QueryOptions` factories):
 *  - Same friction as `projects-query.ts`. Orval models the response as
 *    `{ data: T, status, headers }`, but our `customFetch` returns the parsed
 *    JSON body directly. Cast at queryFn level matches the actual runtime shape.
 *  - Orval factories type `queryFn` as `QueryFunction | typeof skipToken`,
 *    incompatible with `useSuspenseQuery`.
 *
 * Endpoint inventory (verified against generated/endpoints.ts):
 *  - `appsWorkordersApiListWorkOrders({ project_id, limit: 500 })` → the project's
 *    work orders, counted by status on the card and read by «Що далі».
 *  - `appsDeficitsApiGetProjectDeficits(projectId)` → `DeficitsResponse` —
 *    `summary.parts_with_deficit` and `summary.distinct_assemblies_required`
 *    cover two KPIs from one request, and `parts_index[].material_code` gives
 *    the materials the project's BOM actually uses (the global catalog count
 *    used to stand in for it — 36 instead of TERN's 11).
 */

/** Distinct materials of the parts the project's BOM requires. */
export function projectMaterialCount(deficits: DeficitsResponse): number {
  return new Set(deficits.parts_index.map((p) => p.material_code).filter(Boolean)).size
}

const DASHBOARD_WO_LIMIT = 500

export const dashboardWorkOrdersQuery = (projectId: number) =>
  queryOptions({
    queryKey: getAppsWorkordersApiListWorkOrdersQueryKey({
      project_id: projectId,
      limit: DASHBOARD_WO_LIMIT,
      offset: 0,
    }),
    queryFn: async ({ signal }) =>
      (await appsWorkordersApiListWorkOrders(
        { project_id: projectId, limit: DASHBOARD_WO_LIMIT, offset: 0 },
        { signal },
      )) as unknown as PaginatedWorkOrders,
    staleTime: 30_000,
    enabled: Number.isFinite(projectId) && projectId > 0,
  })

export const dashboardDeficitsQuery = (projectId: number) =>
  queryOptions({
    queryKey: getAppsDeficitsApiGetProjectDeficitsQueryKey(projectId),
    queryFn: async ({ signal }) =>
      (await appsDeficitsApiGetProjectDeficits(projectId, {
        signal,
      })) as unknown as DeficitsResponse,
    staleTime: 30_000,
    enabled: Number.isFinite(projectId) && projectId > 0,
  })
